import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { likeApi, LikeStatus, LikeTarget } from '@/services/api/likes'
import { gigApi } from '@/services/api/gigs'
import { showSnackbar } from './uiSlice'

interface LikesState {
    likeStatuses: Record<string, LikeStatus> // key format: "${targetType}:${targetId}"
    userFavoritesCount: number
    isLoading: boolean
    error: string | null
}

const initialState: LikesState = {
    likeStatuses: {},
    userFavoritesCount: 0,
    isLoading: false,
    error: null
}

// Helper function to create cache key
const createCacheKey = (targetType: string, targetId: string): string =>
    `${targetType}:${targetId}`

// Async thunks
export const toggleLike = createAsyncThunk(
    'likes/toggle',
    async (data: { targetId: string; targetType: 'user' | 'gig' | 'review' }, { rejectWithValue, dispatch }) => {
        try {
            const response = await likeApi.toggleLike(data)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to toggle like'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const fetchUserFavoritesCount = createAsyncThunk(
    'likes/fetchUserFavoritesCount',
    async (_, { rejectWithValue }) => {
        try {
            // Use same approach as favorites page - get reasonable limit to ensure accurate totalCount
            const response = await gigApi.getFavoriteGigs({ page: 1, limit: 10 })
            // @ts-ignore - Handle nested response
            const count = response.data.data.totalCount || 0
            return count
        } catch (error: any) {
            return rejectWithValue(0)
        }
    }
)

export const fetchLikeStatus = createAsyncThunk(
    'likes/fetchStatus',
    async (data: { targetType: string; targetId: string }, { rejectWithValue }) => {
        try {
            const response = await likeApi.getLikeStatus(data.targetType, data.targetId)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch like status'
            return rejectWithValue(message)
        }
    }
)

export const fetchBatchLikeStatus = createAsyncThunk(
    'likes/fetchBatchStatus',
    async (targets: LikeTarget[], { rejectWithValue }) => {
        try {
            const response = await likeApi.getBatchLikeStatus(targets)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data.results
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch batch like status'
            return rejectWithValue(message)
        }
    }
)

export const removeLike = createAsyncThunk(
    'likes/remove',
    async (data: { targetType: string; targetId: string }, { rejectWithValue, dispatch }) => {
        try {
            const response = await likeApi.removeLike(data.targetType, data.targetId)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to remove like'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

// Convenience thunks for specific target types
export const toggleUserLike = createAsyncThunk(
    'likes/toggleUser',
    async (userId: string, { rejectWithValue, dispatch }) => {
        try {
            const response = await likeApi.toggleUserLike(userId)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to toggle user like'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const toggleGigLike = createAsyncThunk(
    'likes/toggleGig',
    async (gigId: string, { rejectWithValue, dispatch }) => {
        try {
            const response = await likeApi.toggleGigLike(gigId)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to toggle gig like'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const fetchUserLikeStatus = createAsyncThunk(
    'likes/fetchUserStatus',
    async (userId: string, { rejectWithValue }) => {
        try {
            const response = await likeApi.getUserLikeStatus(userId)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch user like status'
            return rejectWithValue(message)
        }
    }
)

const likesSlice = createSlice({
    name: 'likes',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null
        },
        updateLikeStatus: (state, action: PayloadAction<LikeStatus>) => {
            const { targetType, targetId } = action.payload
            const cacheKey = createCacheKey(targetType, targetId)
            state.likeStatuses[cacheKey] = action.payload
        },
        clearLikeStatuses: (state) => {
            state.likeStatuses = {}
        },
        removeLikeStatus: (state, action: PayloadAction<{ targetType: string; targetId: string }>) => {
            const { targetType, targetId } = action.payload
            const cacheKey = createCacheKey(targetType, targetId)
            delete state.likeStatuses[cacheKey]
        },
        // Optimistic update for better UX
        optimisticToggleLike: (state, action: PayloadAction<{ targetType: string; targetId: string }>) => {
            const { targetType, targetId } = action.payload
            const cacheKey = createCacheKey(targetType, targetId)
            const currentStatus = state.likeStatuses[cacheKey]

            if (currentStatus) {
                state.likeStatuses[cacheKey] = {
                    ...currentStatus,
                    liked: !currentStatus.liked,
                    likesCount: currentStatus.liked
                        ? Math.max(0, currentStatus.likesCount - 1)
                        : currentStatus.likesCount + 1
                }
            } else {
                // Create new status if doesn't exist
                state.likeStatuses[cacheKey] = {
                    targetType: targetType as 'user' | 'gig' | 'review',
                    targetId,
                    liked: true,
                    likesCount: 1
                }
            }
        }
    },
    extraReducers: (builder) => {
        // Toggle Like
        builder
            .addCase(toggleLike.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(toggleLike.fulfilled, (state, action) => {
                state.isLoading = false
                const likeStatus = action.payload
                const cacheKey = createCacheKey(likeStatus.targetType, likeStatus.targetId)
                state.likeStatuses[cacheKey] = likeStatus
            })
            .addCase(toggleLike.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Like Status
        builder
            .addCase(fetchLikeStatus.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchLikeStatus.fulfilled, (state, action) => {
                state.isLoading = false
                const likeStatus = action.payload
                const cacheKey = createCacheKey(likeStatus.targetType, likeStatus.targetId)
                state.likeStatuses[cacheKey] = likeStatus
            })
            .addCase(fetchLikeStatus.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Batch Like Status
        builder
            .addCase(fetchBatchLikeStatus.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchBatchLikeStatus.fulfilled, (state, action) => {
                state.isLoading = false
                const results = action.payload

                results.forEach((likeStatus: LikeStatus) => {
                    const cacheKey = createCacheKey(likeStatus.targetType, likeStatus.targetId)
                    state.likeStatuses[cacheKey] = likeStatus
                })
            })
            .addCase(fetchBatchLikeStatus.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Remove Like
        builder
            .addCase(removeLike.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(removeLike.fulfilled, (state, action) => {
                state.isLoading = false
                const likeStatus = action.payload
                const cacheKey = createCacheKey(likeStatus.targetType, likeStatus.targetId)
                state.likeStatuses[cacheKey] = likeStatus
            })
            .addCase(removeLike.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Toggle User Like
        builder
            .addCase(toggleUserLike.fulfilled, (state, action) => {
                const likeStatus = action.payload
                const cacheKey = createCacheKey(likeStatus.targetType, likeStatus.targetId)
                state.likeStatuses[cacheKey] = likeStatus
            })

        // Toggle Gig Like
        builder
            .addCase(toggleGigLike.fulfilled, (state, action) => {
                const likeStatus = action.payload
                const cacheKey = createCacheKey(likeStatus.targetType, likeStatus.targetId)
                const previousStatus = state.likeStatuses[cacheKey]
                state.likeStatuses[cacheKey] = likeStatus

                // Update favorites count for gig likes
                if (likeStatus.targetType === 'gig') {
                    if (previousStatus && previousStatus.liked && !likeStatus.liked) {
                        // Unliked - decrease count
                        state.userFavoritesCount = Math.max(0, state.userFavoritesCount - 1)
                    } else if ((!previousStatus || !previousStatus.liked) && likeStatus.liked) {
                        // Liked - increase count
                        state.userFavoritesCount += 1
                    }
                }
            })

        // Fetch User Like Status
        builder
            .addCase(fetchUserLikeStatus.fulfilled, (state, action) => {
                const likeStatus = action.payload
                const cacheKey = createCacheKey(likeStatus.targetType, likeStatus.targetId)
                state.likeStatuses[cacheKey] = likeStatus
            })

        // Fetch User Favorites Count
        builder
            .addCase(fetchUserFavoritesCount.fulfilled, (state, action) => {
                console.log('✅ Favorites count updated in state:', action.payload)
                state.userFavoritesCount = action.payload
            })
            .addCase(fetchUserFavoritesCount.rejected, (state) => {
                console.log('❌ Favorites count rejected, setting to 0')
                state.userFavoritesCount = 0
            })
    }
})

export const {
    clearError,
    updateLikeStatus,
    clearLikeStatuses,
    removeLikeStatus,
    optimisticToggleLike
} = likesSlice.actions

export default likesSlice.reducer

// Selectors
const selectAllLikeStatuses = (state: any) => state.likes.likeStatuses

// Create a memoized selector factory for like status
export const selectLikeStatus = (targetType: string, targetId: string) =>
    createSelector(
        [selectAllLikeStatuses],
        (likeStatuses) => {
            const cacheKey = createCacheKey(targetType, targetId)
            return likeStatuses[cacheKey] || {
                liked: false,
                likesCount: 0,
                targetType,
                targetId
            }
        }
    )

export const selectIsLoading = (state: any) => state.likes.isLoading
export const selectError = (state: any) => state.likes.error
export { selectAllLikeStatuses }